import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Smartphone, ChevronDown, QrCode, RefreshCw } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from './ui/input-otp';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const COUNTRY_CODES = [
  { code: '+31', country: 'NL', flag: '🇳🇱' },
  { code: '+32', country: 'BE', flag: '🇧🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+45', country: 'DK', flag: '🇩🇰' },
  { code: '+46', country: 'SE', flag: '🇸🇪' },
  { code: '+47', country: 'NO', flag: '🇳🇴' },
  { code: '+48', country: 'PL', flag: '🇵🇱' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+90', country: 'TR', flag: '🇹🇷' },
  { code: '+212', country: 'MA', flag: '🇲🇦' },
  { code: '+213', country: 'DZ', flag: '🇩🇿' },
  { code: '+216', country: 'TN', flag: '🇹🇳' },
  { code: '+966', country: 'SA', flag: '🇸🇦' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+1', country: 'US', flag: '🇺🇸' },
];

const Auth = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/home';
  const { t, login, register, API } = useApp();
  
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('credentials'); // credentials, 2fa, setup-2fa, qr-login
  const [qrCode, setQrCode] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState(COUNTRY_CODES[0]);
  
  // QR Login state
  const [qrSessionId, setQrSessionId] = useState(null);
  const [qrLoginCode, setQrLoginCode] = useState(null);
  const [pollingQr, setPollingQr] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  // Generate QR code for login
  const generateQrLogin = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/qr/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'QR genereren mislukt');
      
      setQrSessionId(data.session_id);
      setQrLoginCode(data.qr_code);
      setStep('qr-login');
      setPollingQr(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Poll for QR login status
  const pollQrStatus = useCallback(async () => {
    if (!qrSessionId || !pollingQr) return;
    
    try {
      const response = await fetch(`${API}/auth/qr/status/${qrSessionId}`);
      const data = await response.json();
      
      if (data.status === 'authenticated' && data.token) {
        setPollingQr(false);
        localStorage.setItem('token', data.token);
        toast.success('Succesvol ingelogd via QR!');
        window.location.href = redirect;
      }
    } catch (error) {
      // Session expired or error
      if (pollingQr) {
        setPollingQr(false);
        toast.error('QR sessie verlopen');
        setStep('credentials');
      }
    }
  }, [qrSessionId, pollingQr, API, redirect]);

  // Set up polling interval
  useEffect(() => {
    let interval;
    if (pollingQr && qrSessionId) {
      interval = setInterval(pollQrStatus, 2000); // Poll every 2 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pollingQr, qrSessionId, pollQrStatus]);

  // Remove leading 0 from phone number
  const handlePhoneChange = (value) => {
    let phone = value;
    // Remove leading 0
    if (phone.startsWith('0')) {
      phone = phone.substring(1);
    }
    setFormData({ ...formData, phone });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await fetch(`${API}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            totp_code: totpCode || null
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Login failed');
        }
        
        // Check if 2FA is required
        if (data.require_2fa) {
          setStep('2fa');
          toast.info('Voer je 2FA code in');
          setLoading(false);
          return;
        }
        
        // Login successful
        localStorage.setItem('token', data.token);
        toast.success('Welkom terug!');
        window.location.href = redirect;
      } else {
        // Register - Validate full name first
        if (!formData.first_name || formData.first_name.trim().length < 2) {
          toast.error('Voer uw volledige voornaam in (minimaal 2 tekens)');
          setLoading(false);
          return;
        }
        if (!formData.last_name || formData.last_name.trim().length < 2) {
          toast.error('Voer uw volledige achternaam in (minimaal 2 tekens)');
          setLoading(false);
          return;
        }
        
        // Validate name contains only letters
        const namePattern = /^[a-zA-ZÀ-ÿ\s\-']+$/;
        if (!namePattern.test(formData.first_name) || !namePattern.test(formData.last_name)) {
          toast.error('Namen mogen alleen letters bevatten');
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${API}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            country_code: selectedCountryCode.code
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Registration failed');
        }
        
        // Store token and show 2FA setup
        setTempToken(data.token);
        setQrCode(data.qr_code);
        setTotpSecret(data.totp_secret);
        setStep('setup-2fa');
        toast.success('Account aangemaakt! Stel nu 2FA in.');
      }
    } catch (error) {
      toast.error(error.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (totpCode.length !== 6) {
      toast.error('Voer een geldige 6-cijferige code in');
      return;
    }

    setLoading(true);
    try {
      if (step === 'setup-2fa') {
        // Enable 2FA for new user
        const response = await fetch(`${API}/auth/enable-2fa`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tempToken}`
          },
          body: JSON.stringify({ code: totpCode })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Verificatie mislukt');
        }
        
        localStorage.setItem('token', tempToken);
        toast.success('2FA ingeschakeld! Je bent nu ingelogd.');
        window.location.href = redirect;
      } else {
        // Login with 2FA
        const response = await fetch(`${API}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            totp_code: totpCode
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Ongeldige 2FA code');
        }
        
        localStorage.setItem('token', data.token);
        toast.success('Welkom terug!');
        window.location.href = redirect;
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12 relative grain"
      style={{ background: 'radial-gradient(circle at center, #2C1B18 0%, #050505 100%)' }}
    >
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1757313239816-9bac11170af8?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <Link to="/" className="block text-center mb-8">
          <h1 className="font-heading text-3xl">
            <span className="gold-text">Zuiver</span>
            <span className="text-white">Parfum</span>
          </h1>
        </Link>

        {/* Form Card */}
        <div className="glass p-8 rounded-2xl">
          {/* Credentials Step */}
          {step === 'credentials' && (
            <>
              <h2 className="font-heading text-2xl text-white text-center mb-6">
                {isLogin ? t('login') : t('register')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                {!isLogin && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <input
                          type="text"
                          placeholder="Voornaam"
                          value={formData.first_name}
                          onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                          className="input-luxury"
                          required
                          data-testid="first-name-input"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Achternaam"
                          value={formData.last_name}
                          onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                          className="input-luxury"
                          required
                          data-testid="last-name-input"
                        />
                      </div>
                    </div>
                    {/* Phone with Country Code */}
                    <div className="flex gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button 
                            type="button"
                            className="flex items-center gap-1 px-3 py-2 border-b border-[#57534E] text-white hover:border-[#D4AF37] transition-colors"
                          >
                            <span>{selectedCountryCode.flag}</span>
                            <span className="text-sm">{selectedCountryCode.code}</span>
                            <ChevronDown size={14} className="text-[#57534E]" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#1C1917] border-[#2C1B18] max-h-60 overflow-y-auto">
                          {COUNTRY_CODES.map((country) => (
                            <DropdownMenuItem
                              key={country.code}
                              onClick={() => setSelectedCountryCode(country)}
                              className="text-white hover:text-[#D4AF37] hover:bg-[#2C1B18] cursor-pointer"
                            >
                              <span className="mr-2">{country.flag}</span>
                              <span className="mr-2">{country.country}</span>
                              <span className="text-[#57534E]">{country.code}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <input
                        type="tel"
                        placeholder="Telefoonnummer"
                        value={formData.phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        className="input-luxury flex-1"
                        data-testid="phone-input"
                      />
                    </div>
                    <p className="text-[#57534E] text-xs -mt-2">
                      Eerste 0 wordt automatisch verwijderd
                    </p>
                  </>
                )}

                <div>
                  <input
                    type="email"
                    placeholder={t('email_placeholder')}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="input-luxury"
                    required
                    data-testid="email-input"
                  />
                </div>

                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('password_placeholder')}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="input-luxury pr-10"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[#57534E] hover:text-white"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <div className="spinner"></div>
                  ) : (
                    isLogin ? t('login') : t('register')
                  )}
                </button>
              </form>

              {/* Toggle */}
              <div className="mt-6 text-center">
                <p className="text-[#A8A29E]">
                  {isLogin ? t('no_account') : t('have_account')}
                  <button
                    onClick={() => setIsLogin(!isLogin)}
                    className="text-[#D4AF37] hover:underline ml-2"
                  >
                    {isLogin ? t('register') : t('login')}
                  </button>
                </p>
              </div>

              {/* QR Login Option (only for login) */}
              {isLogin && (
                <div className="mt-6 pt-6 border-t border-[#2C1B18]">
                  <button
                    onClick={generateQrLogin}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-[#2C1B18] hover:border-[#D4AF37] text-[#A8A29E] hover:text-[#D4AF37] transition-colors"
                    data-testid="qr-login-btn"
                  >
                    <QrCode size={20} />
                    <span>{t('qr_code_login')}</span>
                  </button>
                  <p className="text-[#57534E] text-xs text-center mt-2">
                    {t('scan_qr_instructions')}
                  </p>
                </div>
              )}
            </>
          )}

          {/* 2FA Login Step */}
          {step === '2fa' && (
            <div className="space-y-6 text-center">
              <Smartphone size={48} className="mx-auto text-[#D4AF37]" />
              <h2 className="font-heading text-xl text-white">2FA</h2>
              <p className="text-[#A8A29E] text-sm">
                {t('enter_2fa_code')}
              </p>
              
              <InputOTP
                maxLength={6}
                value={totpCode}
                onChange={setTotpCode}
                className="justify-center"
              >
                <InputOTPGroup>
                  {[0,1,2,3,4,5].map(i => (
                    <InputOTPSlot key={i} index={i} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                  ))}
                </InputOTPGroup>
              </InputOTP>

              <button
                onClick={handleVerify2FA}
                disabled={loading || totpCode.length !== 6}
                className="btn-primary w-full flex items-center justify-center"
              >
                {loading ? <div className="spinner"></div> : t('verify_code')}
              </button>

              <button
                onClick={() => { setStep('credentials'); setTotpCode(''); }}
                className="text-[#A8A29E] hover:text-white text-sm"
              >
                ← Terug
              </button>
            </div>
          )}

          {/* 2FA Setup Step (after registration) */}
          {step === 'setup-2fa' && (
            <div className="space-y-6 text-center">
              <Smartphone size={48} className="mx-auto text-[#D4AF37]" />
              <h2 className="font-heading text-xl text-white">2FA Instellen</h2>
              <p className="text-[#A8A29E] text-sm">
                Scan deze QR code met Microsoft Authenticator om je account te beveiligen
              </p>
              
              {qrCode && (
                <img 
                  src={qrCode} 
                  alt="QR Code" 
                  className="mx-auto w-48 h-48 bg-white p-2 rounded-lg"
                />
              )}
              
              {totpSecret && (
                <p className="text-[#57534E] text-xs break-all">
                  Of voer handmatig in: {totpSecret}
                </p>
              )}

              <div>
                <p className="text-[#A8A29E] text-sm mb-3">
                  Voer de 6-cijferige code in om te verifiëren
                </p>
                <InputOTP
                  maxLength={6}
                  value={totpCode}
                  onChange={setTotpCode}
                  className="justify-center"
                >
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => (
                      <InputOTPSlot key={i} index={i} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <button
                onClick={handleVerify2FA}
                disabled={loading || totpCode.length !== 6}
                className="btn-primary w-full flex items-center justify-center"
              >
                {loading ? <div className="spinner"></div> : '2FA Inschakelen & Inloggen'}
              </button>
            </div>
          )}

          {/* QR Code Login Step */}
          {step === 'qr-login' && (
            <div className="space-y-6 text-center">
              <QrCode size={48} className="mx-auto text-[#D4AF37]" />
              <h2 className="font-heading text-xl text-white">Scan QR Code</h2>
              <p className="text-[#A8A29E] text-sm">
                Scan deze code met de ZuiverParfum app op een apparaat waarop je al bent ingelogd
              </p>
              
              {qrLoginCode && (
                <div className="relative">
                  <img 
                    src={qrLoginCode} 
                    alt="Login QR Code" 
                    className="mx-auto w-56 h-56 bg-white p-3 rounded-xl"
                  />
                  {pollingQr && (
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-[#1C1917] px-3 py-1 rounded-full">
                      <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse"></div>
                      <span className="text-[#A8A29E] text-xs">Wachten op scan...</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex flex-col gap-2">
                <button
                  onClick={generateQrLogin}
                  disabled={loading}
                  className="text-[#D4AF37] hover:text-white text-sm flex items-center justify-center gap-1"
                >
                  <RefreshCw size={14} />
                  Nieuwe QR code genereren
                </button>
                
                <button
                  onClick={() => { 
                    setStep('credentials'); 
                    setPollingQr(false); 
                    setQrSessionId(null);
                    setQrLoginCode(null);
                  }}
                  className="text-[#A8A29E] hover:text-white text-sm"
                >
                  ← Terug naar inloggen
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-[#A8A29E] hover:text-[#D4AF37] text-sm">
            ← {t('back_to_home')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Auth;
